import { Grid, TextField } from '@mui/material';
import { MenuItem } from '@mui/material';
import { InputAdornment } from '@mui/material';
import { Box, Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { createSGRWarrantyRequestPruffConnect } from 'lib/api/sgr';
import BusinessInput from 'lib/common/BusinessInput';
import { CURRENCIES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import { validaRut } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import RiskPremiumCalculator from './RiskPremiumCalculator';

export type Quote = {
  type: string;
  quotedAmount: string;
  quotedCurrency: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
  unitCode: string | null;
  conversionRate: number;
};

const initialQuote: Quote = {
  type: '',
  quotedAmount: '',
  quotedCurrency: 'CLP',
  startDate: null,
  endDate: null,
  tenderId: '',
  principalRut: '',
  unitCode: null,
  conversionRate: 1,
};

type Props = {
  direction: 'row' | 'column';
  successFn: () => void;
};

const WarrantyForm = ({ direction = 'column', successFn }: Props) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = createSGRWarrantyRequestPruffConnect(dispatch);
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier;
  const [quote, setQuote] = useState<Quote>(initialQuote);
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const isFormValid = Boolean(
    quote.type &&
      quote.quotedAmount &&
      quote.startDate &&
      quote.endDate &&
      quote.principalRut &&
      !rutError &&
      !dateError,
  );

  const updateQuote = (updates: Partial<Quote>) => {
    setQuote((prev) => ({ ...prev, ...updates }));
  };

  const createQuotation = async () => {
    if (!businessIdentifier) throw new Error('Business identifier not found');
    const body = {
      type: quote.type,
      quotedCurrency: quote.quotedCurrency,
      quotedAmount: Number(quote.quotedAmount),
      startDate: quote.startDate,
      endDate: quote.endDate,
      tenderId: quote.tenderId,
      principalRut: quote.principalRut,
      conversionRate: quote.conversionRate,
      unitCode: quote.unitCode,
      businessIdentifier,
    };
    mutate(
      {
        data: body,
        secretId,
      },
      {
        onSuccess: () => {
          setQuote(initialQuote);
          setRutError(null);
          setDateError(null);
          successFn();
        },
      },
    );
  };

  useEffect(() => {
    if (quote.startDate && quote.endDate) {
      if (quote.endDate.isBefore(quote.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [quote.startDate, quote.endDate]);

  useEffect(() => {
    if (quote.principalRut && !validaRut(quote.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [quote.principalRut]);

  return (
    <Stack direction={direction} gap={2}>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID Licitación"
              value={quote.tenderId}
              onChange={(e) => updateQuote({ tenderId: e.target.value })}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <BusinessInput
              identifier={quote.principalRut}
              unitCode={quote.unitCode ?? undefined}
              onChange={(principalRut, unitCode) =>
                updateQuote({ principalRut, unitCode })
              }
              required
              express
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Tipo de Garantía"
              value={quote.type}
              onChange={(e) => updateQuote({ type: e.target.value })}
            >
              <MenuItem value="offer-seriousness">Seriedad de oferta</MenuItem>
              <MenuItem value="faithful-compliance">Fiel cumplimiento</MenuItem>
              <MenuItem value="advance"> Anticipo </MenuItem>
              <MenuItem value="proper-performance">Correcta ejecución</MenuItem>
              <MenuItem value="faithful-compliance-performance">
                Fiel cumplimiento y correcta ejecución
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Moneda"
              value={quote.quotedCurrency}
              onChange={(e) =>
                updateQuote({
                  quotedCurrency: e.target.value,
                })
              }
            >
              {CURRENCIES.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Monto Cotizado"
              type="number"
              value={parseFloat(quote.quotedAmount) || ''}
              onChange={(e) => updateQuote({ quotedAmount: e.target.value })}
              inputProps={{ step: 'any' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {quote.quotedCurrency}
                  </InputAdornment>
                ),
              }}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha Inicio"
                value={quote.startDate}
                onChange={(date) => updateQuote({ startDate: date })}
                minDate={dayjs().subtract(3, 'day')}
                maxDate={dayjs().add(3, 'day')}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha Término"
                value={quote.endDate}
                onChange={(date) => updateQuote({ endDate: date })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <RiskPremiumCalculator
          amount={parseFloat(quote.quotedAmount) || 0}
          startDate={quote.startDate?.toISOString()}
          endDate={quote.endDate?.toISOString()}
          quotedCurrency={quote.quotedCurrency}
          type={quote.type}
        />
        <Stack direction="row" gap={2} justifyContent="flex-end" mt={2}>
          <CustomButton
            color="primary"
            onClick={createQuotation}
            disabled={!isFormValid}
            loading={isPending}
          >
            Crear Cotización
          </CustomButton>
        </Stack>
      </Box>
    </Stack>
  );
};

export default WarrantyForm;
